// Flight database health check and monitoring
import { FLIGHT_DATABASE } from "../data/flights";

export const FLIGHT_DATABASE_CONFIG = {
  MINIMUM_FLIGHTS: 100,
  MINIMUM_AIRLINES: 8,
  MINIMUM_AIRPORTS: 15,
  EXPECTED_FLIGHTS: 110 // Current comprehensive count
};

export interface FlightDatabaseHealth {
  isHealthy: boolean;
  totalFlights: number;
  arrivals: number;
  departures: number;
  airlines: number;
  airports: number;
  issues: string[];
  warnings: string[];
}

/**
 * Check flight database health and return detailed status
 */
export function checkFlightDatabaseHealth(): FlightDatabaseHealth {
  const arrivals = FLIGHT_DATABASE.arrivals.length;
  const departures = FLIGHT_DATABASE.departures.length;
  const totalFlights = arrivals + departures;

  const airlines = new Set(
    [...FLIGHT_DATABASE.arrivals, ...FLIGHT_DATABASE.departures].map((f) => f.airline)
  ).size;

  const airports = new Set([
    ...FLIGHT_DATABASE.arrivals.map((f) => f.origin),
    ...FLIGHT_DATABASE.departures.map((f) => f.destination)
  ]).size;

  const issues: string[] = [];
  const warnings: string[] = [];

  // Critical issues
  if (totalFlights < FLIGHT_DATABASE_CONFIG.MINIMUM_FLIGHTS) {
    issues.push(
      `Flight database has only ${totalFlights} flights (minimum: ${FLIGHT_DATABASE_CONFIG.MINIMUM_FLIGHTS})`
    );
  }

  if (airlines < FLIGHT_DATABASE_CONFIG.MINIMUM_AIRLINES) {
    issues.push(
      `Only ${airlines} airlines configured (minimum: ${FLIGHT_DATABASE_CONFIG.MINIMUM_AIRLINES})`
    );
  }

  // Warnings
  if (totalFlights < FLIGHT_DATABASE_CONFIG.EXPECTED_FLIGHTS * 0.9) {
    warnings.push(
      `Flight count (${totalFlights}) is below expected (${FLIGHT_DATABASE_CONFIG.EXPECTED_FLIGHTS})`
    );
  }

  if (airports < FLIGHT_DATABASE_CONFIG.MINIMUM_AIRPORTS) {
    warnings.push(
      `Only ${airports} airports connected (minimum: ${FLIGHT_DATABASE_CONFIG.MINIMUM_AIRPORTS})`
    );
  }

  const isHealthy = issues.length === 0;

  return {
    isHealthy,
    totalFlights,
    arrivals,
    departures,
    airlines,
    airports,
    issues,
    warnings
  };
}

/**
 * Log health check results to console (development only)
 */
export function logFlightDatabaseHealth(): void {
  if (import.meta.env.PROD) return; // Don't log in production

  const health = checkFlightDatabaseHealth();

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("✈️  Flight Database Health Check");
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log(`Status: ${health.isHealthy ? "✅ HEALTHY" : "⚠️  DEGRADED"}`);
  console.log(
    `Total Flights: ${health.totalFlights} (${health.arrivals} arrivals, ${health.departures} departures)`
  );
  console.log(`Airlines: ${health.airlines}`);
  console.log(`Airports: ${health.airports}`);

  if (health.issues.length > 0) {
    console.log("\n🚨 CRITICAL ISSUES:");
    health.issues.forEach((issue) => console.log(`  ❌ ${issue}`));
  }

  if (health.warnings.length > 0) {
    console.log("\n⚠️  WARNINGS:");
    health.warnings.forEach((warning) => console.log(`  ⚠️  ${warning}`));
  }

  if (health.isHealthy && health.warnings.length === 0) {
    console.log("\n✅ All systems operational");
  }

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
}

/**
 * Validate database on app initialization
 * Throws error in development if database is unhealthy
 */
export function validateFlightDatabase(): void {
  const health = checkFlightDatabaseHealth();

  if (!health.isHealthy) {
    const errorMessage = `
Flight Database Health Check FAILED:
${health.issues.map((i) => `  • ${i}`).join("\n")}

This is a critical issue that will impact customer experience.
Please run: npm run flights:update
    `.trim();

    if (import.meta.env.DEV) {
      // In development, throw error to force developer attention
      console.error(errorMessage);
      throw new Error("Flight database validation failed");
    } else {
      // In production, log error but don't crash the app
      console.error(errorMessage);
    }
  }
}
